import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Perimeter street loops + bridge-deck lanes.
const LOOPS = [
  { pts: [[-232, -252], [188, -252], [188, 212], [-232, 212]], dir: 1, y: 0.7 },
  { pts: [[-208, -228], [164, -228], [164, 188], [-208, 188]], dir: -1, y: 0.7 },
  { pts: [[400, 144], [1300, 144], [1300, 156], [400, 156]], dir: 1, y: 42 }, // Brooklyn Bridge deck
  { pts: [[400, -256], [1300, -256], [1300, -244], [400, -244]], dir: -1, y: 47 }, // Manhattan Bridge deck
];

function buildCurve(pts, dir, y) {
  const v = pts.map(([x, z]) => new THREE.Vector3(x, y, z));
  if (dir < 0) v.reverse();
  return new THREE.CatmullRomCurve3(v, true, "catmullrom", 0.02);
}

const CAR_COLORS = ["#2b2f36", "#c9ccce", "#e8c33a", "#e8c33a", "#7d8790", "#b23a2e", "#37506b"];

export default function Traffic() {
  const carRef = useRef();
  const busRef = useRef();
  const CARS = 46;
  const BUSES = 6;

  const curves = useMemo(() => LOOPS.map((l) => ({ curve: buildCurve(l.pts, l.dir, l.y) })), []);

  const cars = useMemo(
    () =>
      Array.from({ length: CARS }, (_, i) => ({
        loop: i % curves.length,
        u: Math.random(),
        speed: 0.006 + Math.random() * 0.006,
        color: new THREE.Color(CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)]),
      })),
    [curves.length]
  );
  const buses = useMemo(
    () =>
      Array.from({ length: BUSES }, (_, i) => ({
        loop: i % curves.length,
        u: Math.random(),
        speed: 0.004 + Math.random() * 0.003,
      })),
    [curves.length]
  );

  useLayoutEffect(() => {
    if (carRef.current) {
      cars.forEach((c, i) => carRef.current.setColorAt(i, c.color));
      if (carRef.current.instanceColor) carRef.current.instanceColor.needsUpdate = true;
    }
  }, [cars]);

  const dummy = useMemo(() => new THREE.Object3D(), []);
  const p = useMemo(() => new THREE.Vector3(), []);
  const tan = useMemo(() => new THREE.Vector3(), []);

  useFrame((_, delta) => {
    const step = (arr, ref, yScale) => {
      if (!ref.current) return;
      arr.forEach((c, i) => {
        c.u = (c.u + c.speed * delta) % 1;
        const { curve } = curves[c.loop];
        curve.getPointAt(c.u, p);
        curve.getTangentAt(c.u, tan);
        dummy.position.copy(p);
        dummy.rotation.set(0, Math.atan2(tan.x, tan.z), 0);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        ref.current.setMatrixAt(i, dummy.matrix);
      });
      ref.current.instanceMatrix.needsUpdate = true;
    };
    step(cars, carRef, 1);
    step(buses, busRef, 1);
  });

  return (
    <group>
      <instancedMesh ref={carRef} args={[null, null, CARS]}>
        <boxGeometry args={[2.2, 1.5, 4.6]} />
        <meshStandardMaterial roughness={0.4} metalness={0.3} />
      </instancedMesh>
      <instancedMesh ref={busRef} args={[null, null, BUSES]}>
        <boxGeometry args={[2.6, 3.0, 11]} />
        <meshStandardMaterial color="#e8e6df" roughness={0.5} metalness={0.15} />
      </instancedMesh>
    </group>
  );
}
